
import React, { useState } from 'react';
import { Info, Users, Coffee, Trash2, CarFront, CheckCircle2, MapPin, Bath, Star, Lock, ShieldCheck, AlertCircle, Zap, Unlock, Gavel, Search, ExternalLink, Mountain, Landmark, Key, CreditCard, Ban, Camera, Navigation, Footprints, Bike, Snowflake, Route } from 'lucide-react';
import { CampingSpot, WikiSpot, SvvCamera, RouteType } from '../../types';
import { getDistance } from '../../services/geometryUtils';
import { useTranslation } from 'react-i18next';

// Icon + colour per route type, matching the map polyline and the layer-menu legend.
const ROUTE_TYPE_STYLE: Record<RouteType, { icon: React.ComponentType<{ size?: number; className?: string }>; color: string }> = {
  foot: { icon: Footprints, color: 'text-red-500' },
  ski: { icon: Snowflake, color: 'text-sky-500' },
  bike: { icon: Bike, color: 'text-emerald-600' },
  other: { icon: Route, color: 'text-violet-500' },
};

export const HikingTrailsBlock: React.FC<{
  spot: CampingSpot,
  activeTrailId?: string | null,
  onTrailClick?: (id: string | null) => void
}> = ({ spot, activeTrailId, onTrailClick }) => {
  const { t } = useTranslation();
  if (!spot.hikingTrails || spot.hikingTrails.length === 0) return null;

  return (
    <div className="bg-orange-500/5 p-5 rounded-3xl border border-orange-500/20 space-y-3 animate-in slide-in-from-top-2 duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-orange-500 font-black text-3xs uppercase tracking-widest">
          <Mountain size={14} /> {t('spot.details.hiking.title')}
        </div>
        <span className="text-4xs font-black uppercase text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-md border border-orange-400/20">
          {t('spot.details.hiking.found', { count: spot.hikingTrails.length })}
        </span>
      </div>
      <div className="space-y-2">
        {spot.hikingTrails.map((trail, idx) => {
          const isActive = trail.id === activeTrailId;
          const totalLengthKm = trail.totalLength ? (trail.totalLength / 1000).toFixed(1) : null;
          const style = trail.routeType ? ROUTE_TYPE_STYLE[trail.routeType] : null;
          const TypeIcon = style?.icon;

          return (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                onTrailClick?.(isActive ? null : trail.id);
              }}
              className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${isActive ? 'bg-orange-500 border-orange-400 text-white shadow-lg' : 'bg-black/20 border-[var(--border-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-surface)]'}`}
            >
              <div className="flex flex-col items-start gap-1 truncate">
                <div className="flex items-center gap-2">
                  {TypeIcon ? (
                    <TypeIcon size={13} className={`flex-shrink-0 ${isActive ? 'text-white' : style!.color}`} />
                  ) : (
                    <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-white animate-pulse' : 'bg-orange-500'}`} />
                  )}
                  <span className={`text-2xs font-bold truncate uppercase tracking-tight ${isActive ? 'text-white' : 'text-[var(--text-primary)]'}`}>
                    {trail.name}
                  </span>
                </div>
                {totalLengthKm && (
                  <div className="flex items-center gap-2 pl-4">
                    <span className={`text-4xs font-black px-1.5 py-0.5 rounded bg-black/30 border border-[var(--border-primary)] ${isActive ? 'text-white' : 'text-orange-400'}`}>
                      {t('spot.details.hiking.length', { length: totalLengthKm })}
                    </span>
                    {trail.externalUrl && (
                      <a
                        href={trail.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={`inline-flex items-center gap-1 text-4xs font-bold underline-offset-2 hover:underline ${isActive ? 'text-white/80' : 'text-[var(--text-secondary)] hover:text-orange-300'
                          }`}
                        title={trail.externalLabel || t('spot.details.hiking.read_more')}
                      >
                        <ExternalLink size={9} />
                        {t('spot.details.hiking.read_more')}
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className={`text-4xs font-black whitespace-nowrap ${isActive ? 'text-white/80' : 'text-[var(--text-muted)]'}`}>
                  {t('spot.details.hiking.distance_away', { count: Math.round(trail.distanceToStart) })}
                </span>
              </div>
            </button>
          );
        })}
        <p className="text-4xs text-[var(--text-muted)] italic mt-1 font-medium text-center">
          {t('spot.details.hiking.click_to_highlight')}
        </p>
      </div>
    </div>
  );
};

export const AttractionsBlock: React.FC<{
  spot: CampingSpot;
  attractions: WikiSpot[];
}> = ({ spot, attractions }) => {
  const { t } = useTranslation();
  if (!attractions || attractions.length === 0) return null;

  return (
    <div className="bg-violet-500/5 p-5 rounded-3xl border border-violet-500/20 space-y-3 animate-in slide-in-from-top-2 duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-violet-500 font-black text-3xs uppercase tracking-widest">
          <Landmark size={14} /> {t('spot.details.attractions.title')}
        </div>
        <span className="text-4xs font-black uppercase text-violet-400 bg-violet-400/10 px-2 py-0.5 rounded-md border border-violet-400/20">
          {t('spot.details.attractions.found', { count: attractions.length })}
        </span>
      </div>
      <div className="space-y-3">
        {attractions.map((attr, idx) => {
          const dist = getDistance(spot.location.latitude, spot.location.longitude, attr.location.latitude, attr.location.longitude);

          return (
            <div
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                window.open(attr.url, '_blank');
              }}
              className="flex gap-3 p-3 rounded-xl border border-[var(--border-primary)] bg-black/20 hover:bg-[var(--bg-surface)] transition-colors cursor-pointer group"
            >
              {attr.thumbnail && (
                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-[var(--border-secondary)]">
                  <img src={attr.thumbnail.source} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="flex justify-between items-start">
                  <h4 className="text-2xs font-bold text-[var(--text-primary)] uppercase tracking-tight truncate group-hover:text-violet-300 transition-colors">
                    {attr.title}
                  </h4>
                  <span className="text-4xs font-black text-[var(--text-muted)] whitespace-nowrap ml-2">
                    {dist > 1000 ? `${(dist / 1000).toFixed(1)} km` : `${Math.round(dist)} m`}
                  </span>
                </div>
                <p className="text-3xs text-[var(--text-muted)] line-clamp-2 mt-0.5 leading-relaxed">
                  {attr.extract}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const LandownerBlock: React.FC<{ spot: CampingSpot }> = ({ spot }) => {
  const { t } = useTranslation();
  if (!spot.property) return null;
  const p = spot.property;
  const isStatskog = p.eierform === 'Statskog';

  const seeEiendomUrl = (p.kommunenummer && p.gnr && p.bnr)
    ? `https://seeeiendom.kartverket.no/eiendom/${p.kommunenummer}/${p.gnr}/${p.bnr}/0/0`
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 text-2xs font-black px-2 py-1 rounded border max-w-full ${isStatskog
          ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
          : 'text-[var(--text-secondary)] border-[var(--border-secondary)] bg-[var(--bg-surface-alt)]'
          }`}
        title={`${t('spot.details.landowner.title')}: ${p.ownerName || '-'} (${p.kommunenavn || '-'})`}
      >
        <Gavel size={11} />
        <span className="truncate max-w-[180px]">{p.ownerName || t('spot.details.landowner.title')}</span>
      </span>

      {p.kommunenavn && (
        <span className="inline-flex items-center gap-1 text-2xs font-black px-2 py-1 rounded border text-[var(--text-secondary)] border-[var(--border-secondary)] bg-[var(--bg-surface-alt)]">
          <MapPin size={10} />
          <span className="truncate max-w-[110px]">{p.kommunenavn}</span>
        </span>
      )}

      {isStatskog && (
        <span className="inline-flex items-center gap-1 text-2xs font-black px-2 py-1 rounded border text-emerald-300 border-emerald-500/35 bg-emerald-500/10">
          <ShieldCheck size={10} />
          {t('spot.details.landowner.statskog')}
        </span>
      )}

      {!isStatskog && seeEiendomUrl && (
        <button
          onClick={(e) => { e.stopPropagation(); window.open(seeEiendomUrl, '_blank'); }}
          className="inline-flex items-center gap-1 text-2xs font-black px-2 py-1 rounded border text-[var(--text-secondary)] border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] transition-all"
          title={t('spot.details.landowner.check_seeeiendom')}
        >
          <Search size={10} />
          <ExternalLink size={10} className="opacity-60" />
        </button>
      )}
    </div>
  );
};

export const ChargerProximityBlock: React.FC<{ spot: CampingSpot }> = ({ spot }) => {
  const { t } = useTranslation();
  if (!spot.nearestCharger) return null;
  const dist = spot.nearestCharger.distance ? Math.round(parseFloat(spot.nearestCharger.distance as any)) : null;
  const city = spot.nearestCharger.city;

  return (
    <div className="bg-yellow-500/5 p-5 rounded-3xl border border-yellow-500/20 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-yellow-500 font-black text-3xs uppercase tracking-widest">
          <Zap size={14} fill="currentColor" /> {t('spot.details.charger.title')}
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Zap size={16} className="text-yellow-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-[var(--text-primary)] text-tiny font-black leading-tight uppercase tracking-tight">
            {t('spot.details.charger.nearest_charger')}: {spot.nearestCharger.name}
            {city && <span className="text-[var(--text-secondary)] ml-1">({city})</span>}
          </p>
          <p className="text-[var(--text-secondary)] text-2xs mt-1 font-black flex items-center gap-2">
            <span className="text-yellow-500 uppercase">{t('spot.details.charger.distance')}:</span>
            {dist !== null ? (dist > 1000 ? `${(dist / 1000).toFixed(1)} km` : `${dist} m`) : t('spot.details.charger.under_5km')}
          </p>
          <p className="text-[var(--text-muted)] text-3xs mt-1 font-medium italic">
            {spot.nearestCharger.address || t('spot.details.charger.address_unknown')}
          </p>
        </div>
      </div>
    </div>
  );
};

export const AccessBarrierBlock: React.FC<{ spot: CampingSpot; onShowOnMap?: () => void }> = ({ spot, onShowOnMap }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { hasBarrier, barrierType } = spot.legal || {};

  if (!hasBarrier) return null;

  return (
    <div
      onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
      className={`cursor-pointer transition-all duration-300 rounded-3xl border ${isExpanded ? 'bg-red-500/10 border-red-500/30 p-5 space-y-3' : 'bg-red-500/5 border-red-500/20 p-3 flex items-center gap-3'}`}
    >
      <div className="flex items-center gap-2 text-red-400 font-black text-3xs uppercase tracking-widest">
        <Lock size={14} className={!isExpanded ? 'shrink-0' : ''} />
        {!isExpanded ? (
          <span className="truncate">{t('spot.details.barrier.registered')}</span>
        ) : (
          <>{t('spot.details.barrier.info_title')}</>
        )}
      </div>

      {isExpanded && (
        <div className="animate-in fade-in slide-in-from-top-2">
          <div className="flex items-start gap-3 mt-2">
            <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-[var(--text-primary)] text-2xs font-bold leading-tight">
                {t('spot.details.barrier.info_desc')}
              </p>
              <div className="mt-2 flex flex-col gap-1">
                <p className="text-[var(--text-secondary)] text-3xs font-medium">{t('spot.details.barrier.type')}: <span className="text-[var(--text-secondary)]">{barrierType || t('spot.details.barrier.default_type')}</span></p>
              </div>
            </div>
          </div>
          {onShowOnMap && (
            <button
              onClick={(e) => { e.stopPropagation(); onShowOnMap(); }}
              className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-2xl bg-red-500/15 border border-red-500/30 text-red-400 text-2xs font-black uppercase tracking-wider hover:bg-red-500/25 transition-colors"
            >
              <Navigation size={12} />
              {t('spot.details.barrier.show_on_map')}
            </button>
          )}
          <p className="text-4xs font-black text-red-500/50 uppercase mt-2 text-center">{t('spot.details.barrier.click_to_close')}</p>
        </div>
      )}
    </div>
  );
};

export const RoadConnectionBlock: React.FC<{ spot: CampingSpot }> = ({ spot }) => {
  const { t } = useTranslation();
  if (!spot.publicRoadConnection) return null;

  const isPrivateType = spot.highway === 'track' || spot.highway === 'unclassified';

  return (
    <div className={`p-5 rounded-3xl border space-y-3 ${isPrivateType ? 'bg-amber-500/5 border-amber-500/20' : 'bg-brand/5 border-brand/20'}`}>
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-2 font-black text-3xs uppercase tracking-widest ${isPrivateType ? 'text-amber-500' : 'text-brand'}`}>
          {isPrivateType ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
          {isPrivateType ? t('spot.details.road_connection.caution') : t('spot.details.road_connection.verified')}
        </div>
        <div className={`px-2 py-0.5 rounded-md text-4xs font-black border uppercase ${isPrivateType ? 'bg-amber-500/20 text-amber-500 border-amber-500/20' : 'bg-brand/20 text-brand border-brand/20'}`}>
          {spot.publicRoadConnection.category}
        </div>
      </div>
      <div className="flex items-start gap-3">
        <MapPin size={16} className={`shrink-0 mt-0.5 ${isPrivateType ? 'text-amber-500' : 'text-brand'}`} />
        <div>
          <p className="text-[var(--text-primary)] text-2xs font-bold leading-tight">
            {spot.publicRoadConnection.categoryName}
          </p>
          <p className="text-[var(--text-muted)] text-3xs mt-1 font-medium italic">
            {t('spot.details.road_connection.description')}
          </p>
          {isPrivateType && (
            <p className="text-amber-500 text-2xs mt-2 font-black uppercase flex items-center gap-1.5 animate-pulse">
              <Ban size={12} /> {t('spot.details.road_connection.landowner_contact')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export const RasteplassFacilities: React.FC<{ spot: CampingSpot }> = ({ spot }) => {
  const { t } = useTranslation();
  if (!spot.rasteplassMetadata) return null;

  const parkingLevel = spot.rasteplassMetadata.kapasitet > 15 ? t('spot.details.facilities.parking.excellent') : spot.rasteplassMetadata.kapasitet > 5 ? t('spot.details.facilities.parking.good') : t('spot.details.facilities.parking.limited');

  return (
    <div className="bg-blue-500/5 p-5 rounded-3xl border border-blue-500/20 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-blue-400 font-black text-3xs uppercase tracking-widest">
          <Star size={14} className="fill-blue-400" /> {t('spot.details.facilities.title')}
        </div>
        <span className="text-4xs font-black uppercase text-blue-300 bg-blue-400/10 px-2 py-0.5 rounded-md border border-blue-400/20">
          {spot.rasteplassMetadata.type}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div className={`flex flex-col items-center gap-1 bg-black/20 p-2 rounded-2xl border border-[var(--border-primary)] ${spot.rasteplassMetadata.kapasitet > 0 ? 'text-blue-400' : 'text-[var(--text-muted)]'}`}>
          <Users size={12} />
          <span className="text-[7px] font-black uppercase">{spot.rasteplassMetadata.kapasitet} {t('spot.details.facilities.parking.label')}</span>
          <span className="text-[6px] opacity-60 font-bold uppercase tracking-tighter">{parkingLevel}</span>
        </div>
        <div className={`flex flex-col items-center gap-1 bg-black/20 p-2 rounded-2xl border border-[var(--border-primary)] ${spot.rasteplassMetadata.mobler ? 'text-blue-400' : 'text-[var(--text-muted)]'}`}>
          <Coffee size={12} />
          <span className="text-[7px] font-black uppercase">{spot.rasteplassMetadata.mobler ? t('spot.details.facilities.furniture.label') : t('spot.details.facilities.furniture.none')}</span>
          <span className="text-[6px] opacity-60 font-bold uppercase tracking-tighter">{t('spot.details.facilities.furniture.title')}</span>
        </div>
        <div className={`flex flex-col items-center gap-1 bg-black/20 p-2 rounded-2xl border border-[var(--border-primary)] ${spot.rasteplassMetadata.toalett ? 'text-blue-400' : 'text-[var(--text-muted)]'}`}>
          <Bath size={12} />
          <span className="text-[7px] font-black uppercase">{spot.rasteplassMetadata.toalett ? t('spot.details.facilities.toilet.label') : t('spot.details.facilities.toilet.none')}</span>
          <span className="text-[6px] opacity-60 font-bold uppercase tracking-tighter">WC</span>
        </div>
        <div className={`flex flex-col items-center gap-1 bg-black/20 p-2 rounded-2xl border border-[var(--border-primary)] ${spot.rasteplassMetadata.soppel ? 'text-blue-400' : 'text-[var(--text-muted)]'}`}>
          <Trash2 size={12} />
          <span className="text-[7px] font-black uppercase">{spot.rasteplassMetadata.soppel ? t('spot.details.facilities.trash.label') : t('spot.details.facilities.trash.none')}</span>
          <span className="text-[6px] opacity-60 font-bold uppercase tracking-tighter">{t('spot.details.facilities.trash.title')}</span>
        </div>
      </div>
    </div>
  );
};

export const WebcamBlock: React.FC<{
  cameras: (SvvCamera & { _distance: number })[];
  failedIds: Set<string>;
  onImageError: (id: string) => void;
}> = ({ cameras, failedIds, onImageError }) => {
  const { t } = useTranslation();
  const visible = cameras.filter(c => !failedIds.has(c.id));
  if (visible.length === 0) return null;

  return (
    <div className="bg-black/40 p-5 rounded-3xl border border-[var(--border-primary)] space-y-3">
      <div className="flex items-center gap-2 text-cyan-400 font-black text-3xs uppercase tracking-widest">
        <Camera size={14} /> {t('spot.details.webcams', { count: visible.length })}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {visible.map(cam => (
          <div key={cam.id} className="relative rounded-xl overflow-hidden border border-[var(--border-primary)]">
            <img
              src={cam.image_url}
              alt={cam.name}
              loading="lazy"
              decoding="async"
              className="w-full h-24 object-cover"
              onError={() => onImageError(cam.id)}
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
              <p className="text-4xs font-bold text-white truncate">{cam.name}</p>
              {cam.road_name && <p className="text-[7px] text-[var(--text-secondary)] truncate">{cam.road_name}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const VideoBlock: React.FC<{ spot: CampingSpot }> = ({ spot }) => {
  const { t } = useTranslation();
  if (!spot.videoUrl) return null;

  let thumbnail: string | null = null;
  try {
    const u = new URL(spot.videoUrl);
    if (u.hostname.match(/(?:youtube\.com|youtu\.be)/)) {
      const videoId = u.hostname === 'youtu.be' ? u.pathname.slice(1) : u.searchParams.get('v');
      if (videoId) thumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    } else if (u.hostname.includes('vimeo.com')) {
      const match = u.pathname.match(/\/(\d+)/);
      if (match) thumbnail = `https://vumbnail.com/${match[1]}.jpg`;
    }
  } catch { /* ignore */ }

  return (
    <a href={spot.videoUrl} target="_blank" rel="noopener noreferrer" className="block bg-black/40 rounded-3xl border border-[var(--border-primary)] overflow-hidden group">
      {thumbnail ? (
        <div className="relative">
          <img src={thumbnail} alt="" loading="lazy" decoding="async" className="w-full h-36 object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
              <i className="fa-solid fa-play text-gray-900 text-lg ml-0.5" />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-5">
          <div className="w-10 h-10 bg-sky-500/20 rounded-xl flex items-center justify-center shrink-0">
            <i className="fa-solid fa-play text-sky-400 text-sm" />
          </div>
          <div className="truncate">
            <p className="text-2xs font-black uppercase tracking-wider text-sky-300">{t('spot.watch_video')}</p>
            <p className="text-3xs text-[var(--text-muted)] truncate">{spot.videoUrl}</p>
          </div>
        </div>
      )}
    </a>
  );
};

export const TrafficWarning: React.FC<{ spot: CampingSpot }> = ({ spot }) => {
  const { t } = useTranslation();
  const trafficLevel = spot.aiResult?.trafikk_vurdering?.niva?.toUpperCase() || 'LAV';
  const desc = (spot.aiResult?.trafikk_vurdering?.beskrivelse || "").toLowerCase();
  const hasNoiseIssues = desc.includes("støy") || desc.includes("trafikk") || desc.includes("støyende") || desc.includes("noise") || desc.includes("traffic");

  if (!((trafficLevel === 'MODERAT' || trafficLevel === 'HOY' || trafficLevel === 'MODERATE' || trafficLevel === 'HIGH') && hasNoiseIssues)) return null;

  return (
    <div className="bg-black/40 p-5 rounded-3xl border border-[var(--border-primary)] space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-400 font-black text-3xs uppercase tracking-widest">
          <CarFront size={14} /> {t('spot.details.traffic.title')}
        </div>
      </div>
      <p className="text-[var(--text-secondary)] text-2xs leading-relaxed">
        {spot.aiResult?.trafikk_vurdering?.beskrivelse}
      </p>
    </div>
  );
};
